import React from 'react';
import { Box, Card, MenuItem, SelectChangeEvent, TextField, Typography } from '@mui/material';
import IbssDataGrid from '../../../../Components/Data/DataGrid/IbssDataGrid';
import IbssFilterHeader from '../../../../Components/Forms/DateRange/IbssFilterHeader';
import IbssFilter from '../../../../Components/Forms/Filter/IbssFilter';
import { appContext } from '../../../../AppContext';
import { RouteComponentProps } from 'react-router';
import { DateTime } from 'luxon';
import ListFilterModal from './ListFilterModal';
import IbssSvgIcon from '../../../../Components/Icons/SvgIcon/IbssSvgIcon';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import PauseIcon from '@mui/icons-material/Pause';
import IbssActionButton, { IActionButton } from '../../../../Components/Buttons/ActionButton/IbssActionButton';
import IbssDialog from '../../../../Components/Dialogs/BaseDialog/IbssDialog';
import IbssButtonRedo from '../../../../Components/Buttons/Button/IbssButton';
import { Icons } from '../../../../Common/AllsvgIcons';
import IbssTextField from '../../../../Components/Inputs/TextField/IbssTextField';
import IbssFormControl from '../../../../Components/Forms/FormControl/IbssFormControl';
import IbssChip from '../../../../Components/Navigation/Chip/IbssChip';
import Helper from '../../../../Common/Helper';
import { DateHelper } from '../../../../Common/DateHelper';
import { IbssLuxonDateTimePicker } from '../../../../Components/Inputs/LuxonDateTimePicker/IbssLuxonDateTimePicker';
import { IUser } from '../../../../Components/Inputs/UserPicker/UserPicker';
import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import { QueryParams } from './QueryParams';
import { Announcement, IAnnouncementUpdate } from './DataModel';
import { PagedResponse } from '../../../../Providers.Api/Models';
import { DateRangeHelper } from '../../../../Common/DateRangeHelper';
import { StringHelper } from '../../../../Common/StringHelper';
import { Constants } from '../../../../Common/Constants';
import { rights } from '../../../../Common/RightsManager';
import ServerDataGrid, { DataGridQueryResult, IDataQuery, IPages } from '../../../../Components/Data/ServerDataGrid/ServerDataGrid';
import { GridRowSelectionModel } from '@mui/x-data-grid';

class ListNotifications extends IbssPage<IProps, IState, QueryParams>
{

    private get labels() { return appContext().labels; }
    private get apiClient() { return appContext().apiClient; }
    private get localStorage() { return appContext().localStorageProvider; }
    private get appState() { return appContext().state; }
    private string = new StringHelper();
    private userCanCreate: boolean;
    private userCanDelete: boolean;
    private userCanUpdate: boolean;
    private announcementCacheKey = '624a435a-3bda-41e7-bb1a-1c9876ba4fb7';
    private dataGridRef = React.createRef<ServerDataGrid<AnnouncementView>>();
    private get dataGrid() { return this.dataGridRef.current; }
    private pageSize: number;

    constructor(props: IProps)
    {
        super(props, new QueryParams());
        this.userCanCreate = rights.DATAMODEL.Notifications.Create.has();
        this.userCanDelete = rights.DATAMODEL.Notifications.Delete.has();
        this.userCanUpdate = rights.DATAMODEL.Notifications.Update.has();
        this.pageSize = 100;
        this.state =
        {
            loading: false,
            dateRangeFilter: this.labels.HubLabelThisWeek,
            fromFilter: DateTime.now(),
            toFilter: DateTime.now(),
            datePickerFiltersDisabled: true,
            searchTerm: '',
            openFilterModal: false,
            broadcastSelectValue: 'any',
            statusSelectValue: 'any',
            selectedAnnouncementCreatorEmail: '',
            selectedAnnouncementCreator: null,
            showDeleteConfirmationModal: false,
            buildingId: this.appState.buildingId,
            showDuplicateNotificationModal: false,
            duplicateNotificationTitle: '',
            showExpireConfirmationModal: false,
            selectedAnnouncements: [],
            duplicateBuildingOptions: new Array<IOption>(),
            startDateTime: DateTime.now(),
            endDateTime: DateTime.now(),
            showDateTimeError: false,
            selectedBuildings: [],
            pagesOfAnnouncements:
            {
                pages: [],
                pageIndex: 0,
                skipToken: '',
            },
        };
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.funcBuildingAnnouncement_S;
        const buildings = Helper.getAllBuildingsData().sort((a, b) => a.Name.localeCompare(b.Name));
        const options = buildings.map(i => ({ value: i.Node_Id, label: i.Name }) as IOption);
        this.setState({ duplicateBuildingOptions: options });
    }

    public async queryParamsDidUpdate(firstLoad: boolean, prevParams: QueryParams): Promise<void>
    {
        const queryParams = this.queryParams;

        if (firstLoad)
        {
            this.clearCache();
        }
        
        if (!queryParams.building)
        {
            return this.pushQueryParams({ building: this.appState.buildingId }, true);
        }
        else if (!queryParams.status)
        {
            return this.pushQueryParams({ status: "any" }, true);
        }
        else if (!queryParams.broadcast)
        {
            return this.pushQueryParams({ broadcast: "any" }, true);
        }

        const dateRange = new DateRangeHelper(queryParams.building ?? 1)
            .addToday()
            .addTomorrow()
            .addThisWeek()
            .addNextWeek()
            .addThisMonth()
            .addNextMonth()
            .getDateRange(queryParams.period, this.labels.HubLabelThisWeek, queryParams.start, queryParams.end);

        if (dateRange.isPeriodCustom && (queryParams.period != undefined || queryParams.start != dateRange.start || queryParams.end != dateRange.end))
        {
            return this.pushQueryParams({ period: undefined, start: dateRange.start, end: dateRange.end }, true);
        }
        else if (!dateRange.isPeriodCustom && (queryParams.period != dateRange.period || queryParams.start != undefined || queryParams.end != undefined))
        {
            return this.pushQueryParams({ period: dateRange.period, start: undefined, end: undefined }, true);
        }

        await this.setStateAsync({
            buildingId: queryParams.building ?? 1,
            dateRangeFilter: dateRange.period,
            datePickerFiltersDisabled: !dateRange.isPeriodCustom,
            fromFilter: dateRange.start,
            toFilter: dateRange.end,
            broadcastSelectValue: queryParams.broadcast ?? '',
            statusSelectValue: queryParams.status ?? '',
            searchTerm: queryParams.filter ?? '',
            selectedAnnouncementCreatorEmail: queryParams.createdBy ?? '',
        });

        await this.dataGrid?.refresh();
    }

    private clearCache(): void
    {
        appContext().inMemoryCache.clear(i => i.startsWith(this.announcementCacheKey + '_'));
    }

    private get isZeroOrOneBuildings() { return this.state.duplicateBuildingOptions.length <= 1; }
    private get selectedBuildingId() { return this.isZeroOrOneBuildings ? this.state.duplicateBuildingOptions[0]?.value : -1; }
    
    private filterQuery(filterStartDate: string, filterEndDate: string, broadcast: string, status: string, creatorEmail: string): string
    {
        let query = `Classification eq 'Building' and Expiry_Date ge datetime'${filterStartDate}' and Available_From le datetime'${filterEndDate}'`;
        
        if(status && status !== 'any')
        {
            query = query + ` and Notification_Status eq '${status}'`;
        }
        if(creatorEmail)
        {
            query = query + ` and CreatedBy eq '${creatorEmail}'`;
        }
        if(broadcast && broadcast !== 'any')
        {
            const target = this.broadcastTargetEncode(broadcast)
            query = query + ` and Notification_Target eq ${target}`;
        }
        return query;
    }

    private broadcastTargetEncode(broadcast: string): number
    {
        switch(broadcast)
        {
            case 'any':
                return 0;
            case 'Roamer and Flex':
                return 1;
            case 'Inspire':
                return 2;
            default:
                return 0;
        }
    }

    private async dataQueryChanged(gridQuery: IDataQuery): Promise<DataGridQueryResult<AnnouncementView>>    
    {
        this.setState({ loading: true });
        let rows = [];
        let skipToken = '';
        try
        {
            const filterStartDate = this.state.fromFilter.startOf('day').toUtcByNode(this.state.buildingId).toISO();
            const filterEndDate = this.state.toFilter.endOf('day').toUtcByNode(this.state.buildingId).toISO();

            const endpoint = 
            {
                name: 'notifications.getMany',
                options: 
                {
                    nodeId: this.state.buildingId,
                    select: Announcement,
                    top: this.pageSize,
                    skipToken: gridQuery.skipToken,
                    filter: this.filterQuery
                    (
                        filterStartDate, 
                        filterEndDate,
                        this.state.broadcastSelectValue,
                        this.state.statusSelectValue,
                        this.state.selectedAnnouncementCreatorEmail.toLocaleLowerCase(),
                    ),
                }
            }


            const response = await appContext().inMemoryCache.lazyGetWithQuickExpiry
            (
                `${this.announcementCacheKey}_${JSON.stringify(endpoint)}`,
                () => appContext().ibssApiClientV2.v2.byNodeid.announcements.get<PagedResponse<Announcement[]>>(endpoint.options)
            );

            const announcements = response.value;
            skipToken = response.skipToken;

            rows = announcements.map(announcement => AnnouncementView.fromAnnouncementToView(announcement));
        }
        finally
        {
            this.setState({ loading: false });
        }
        return new DataGridQueryResult(rows, skipToken);
    }

    private async dateRangeChanged(e: SelectChangeEvent<string>): Promise<void>
    {
        const period = this.string.toKebabCase(e.target.value);
        const customPeriod = this.string.toKebabCase(this.labels.HubLabelCustom);
        const periodIsCustom = (period == customPeriod);

        this.pushQueryParams({
            period: (periodIsCustom ? undefined : period),
            start: (periodIsCustom ? this.state.fromFilter : undefined),
            end: (periodIsCustom ? this.state.toFilter : undefined),
        });
    }

    private async startDateChanged(e: DateTime): Promise<void>
    {
        this.pushQueryParams({ start: e });
    }

    private async endDateChanged(e: DateTime): Promise<void>
    {
        this.pushQueryParams({ end: e });
    }

    private filterAnnouncements(searchTerm: string): IPages<AnnouncementView>
    {
        const rows = this.state.pagesOfAnnouncements.pages.map(x => x.rows).flat();
        const fieldsToFilter: (keyof AnnouncementView)[] = ['id', 'broadcast', 'label', 'title', 'priority', 'createdBy', 'status'];
        const searchTermLower = searchTerm.toLocaleLowerCase();

        if(!searchTerm)
        {
            return this.state.pagesOfAnnouncements;
        }

        const announcements = rows.filter(row =>
        {
            return fieldsToFilter.some(key => row[key]?.toString().toLocaleLowerCase().includes(searchTermLower))
        });

        return { pages: [{ rows: announcements }], skipToken: '', pageIndex: 0 };
    }

    private debounceFilterTextTimer: NodeJS.Timeout | null = null;
    private searchTermChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void
    {
        this.setState({ searchTerm: event.target.value });
        clearTimeout(this.debounceFilterTextTimer ?? undefined);
        this.debounceFilterTextTimer = setTimeout(
            () => this.pushQueryParams({ filter: event.target.value === '' ? undefined : event.target.value }),
            Constants.longDebounceTimeInMilliseconds,
        );
    }

    private filterModalSubmitted(): void
    {
        this.pushQueryParams({
            broadcast: this.state.broadcastSelectValue,
            status: this.state.statusSelectValue,
            createdBy: this.state.selectedAnnouncementCreatorEmail
        });

        this.setState({ openFilterModal: false });
    }

    private async deleteSelectedItems(): Promise<void>
    {
        this.setState({ loading: true, showDeleteConfirmationModal: false });

        try
        {
            const selectedFieldIds = this.state.selectedAnnouncements.map(announcement => announcement.id);
            for (let index = 0; index < selectedFieldIds.length; index++)
            {
                const announcementId = selectedFieldIds[index];
                await appContext().ibssApiClientV2.v2.byNodeid.announcements.byAnnouncementid.delete<IAnnouncementUpdate>
                ({
                    nodeId: this.state.buildingId,
                    announcementid: parseInt(announcementId)
                }); 
            }
            this.clearCache();
            await this.dataGrid?.refresh();
        } catch (error)
        {
            this.setState({ loading: false });
        }
        this.setState({ loading: false, selectedAnnouncements: [] });
    }

    private async createDuplicate(): Promise<void>
    {
        this.setState({ loading: true, showDuplicateNotificationModal: false });
        const currentUser = this.localStorage.getUserDetails();
        const selectedAnnouncementIds = this.state.selectedAnnouncements.map(announcement => parseInt(announcement.id));

        try
        {
            const selectedNotification = await appContext().ibssApiClientV2.v2.byNodeid.announcements.byId.get<Announcement>({
                nodeId: this.state.buildingId,
                id: selectedAnnouncementIds[0],
                select: Announcement,
            });

            for (let index = 0; index < this.state.selectedBuildings.length; index++)
            {
                const buildingId = this.state.selectedBuildings[index];
                const payload = {
                    ImageURI: selectedNotification.ImageURI,
                    Notification_Title: this.state.duplicateNotificationTitle,
                    Notification_Priority: selectedNotification.Notification_Priority,
                    Classification: selectedNotification.Classification,
                    Subclassification: selectedNotification.Subclassification,
                    Notification_Message: selectedNotification.Notification_Message,
                    Notification_Short_Message: selectedNotification.Notification_Short_Message,
                    Available_From: this.state.startDateTime.setZoneByNode(buildingId).toUTC().toISO(),
                    Expiry_Date: this.state.endDateTime.setZoneByNode(buildingId).toUTC().toISO(),
                    Notification_Type: 1,
                    Notification_Status: selectedNotification.Notification_Status,
                    Notification_User_Email: currentUser.email,
                    Notification_Device: selectedNotification.Notification_Device,
                    Notification_Target: selectedNotification.Notification_Target,
                }

                await appContext().ibssApiClientV2.v2.byNodeid.announcements.post<IAnnouncementUpdate>
                ({
                    nodeId: buildingId,
                    body: payload
                });
            }
            this.clearCache();
            await this.dataGrid?.refresh();
        } catch (error)
        {
            this.setState({ loading: false });
        }
        this.setState({
            loading: false,
            selectedAnnouncements: [],
            selectedBuildings: [],
            startDateTime: DateTime.now(),
            endDateTime: DateTime.now()
        });
    }

        private handleSelectionChange(selection: GridRowSelectionModel): void
        {
            const pageOfRows = this.dataGrid?.rows as AnnouncementView[];
            const selectedRows = pageOfRows.filter(row => selection.includes(row.id))
            this.setState({ selectedAnnouncements: selectedRows });
        }
    

    private async expireSelectedItems(): Promise<void>
    {
        this.setState({ loading: true, showExpireConfirmationModal: false });

        const selectedAnnouncementIds = this.state.selectedAnnouncements.map(announcement => announcement.id);
        try
        {
            for (let index = 0; index < selectedAnnouncementIds.length; index++)
            {
                const selectedFieldId = selectedAnnouncementIds[index];
                const pageOfRows = this.dataGrid?.rows as AnnouncementView[];
                const selectedNotification = pageOfRows.filter(x => x.id === selectedFieldId)[0];
                const payload = {
                    ImageURI: selectedNotification.imageUri,
                    Notification_Title: selectedNotification.title,
                    Notification_Priority: selectedNotification.priority.toString(),
                    Classification: selectedNotification.classification,
                    SubClassfication: selectedNotification.subClassification,
                    Notification_Message: selectedNotification.message,
                    Notification_Short_Message: selectedNotification.shortMessage,
                    Available_From: selectedNotification.start.toUtcByNode(this.state.buildingId).toISO(),
                    Expiry_Date: DateTime.now().toISO(),
                    Notification_Type: 1,
                    Notification_Status: 'Inactive',
                    Notification_User_Email: selectedNotification.createdBy,
                }
                await appContext().ibssApiClientV1.v1.byNodeid.notifications.byNotificationId.put
                ({
                    nodeId: this.state.buildingId,
                    notificationId: parseInt(selectedFieldId),
                    body: payload
                });
            }
            this.clearCache();
            await this.dataGrid?.refresh();
        } catch (error)
        {
            this.setState({ loading: false });
        }
        this.setState({ loading: false, selectedAnnouncements: [] });
    }

    public renderNotificationStatus(row: AnnouncementView): React.ReactNode
    {
        if (row.status === 'Active')
        {
            return <IbssChip label={this.labels.HubLabelActive} sx={{
                backgroundColor: 'var(--ui-success)',
                color: "var(--ui-light-text)",
            }} />;
        }
        else if (row.status === 'Inactive')
        {
            return <IbssChip label={this.labels.HubLabelInActive} sx={{
                backgroundColor: 'var(--ui-text-alternate)',
                color: "var(--ui-light-text)",
            }} />;
        }
        else
        {
            return <p>-</p>;
        }
    }

    public getPriorityLabel(priorityValue: number): string
    {
        switch (priorityValue)
        {
            case 1:
                return this.labels.HubLabelHighPriority
            case 2:
                return this.labels.funcAnouncementPriorityMedium_Short
            case 3:
                return this.labels.HubLabelLowPriority
            default:
                return '-'
        }
    }

    private buildingClicked(selectedBuildingId: number): void
    {
        if (!this.state.selectedBuildings.includes(selectedBuildingId))
        {
            this.setState({ selectedBuildings: [...this.state.selectedBuildings, selectedBuildingId] });
        }
    }

    private duplicateAnnouncementClicked(): void
    {
        const selectedAnnouncement = this.state.selectedAnnouncements[0];

        this.setState({
            showDuplicateNotificationModal: true,
            selectedBuildings: this.isZeroOrOneBuildings ? [this.state.duplicateBuildingOptions[0].value] : [],
            duplicateBuildingOptions: this.isZeroOrOneBuildings ? [this.state.duplicateBuildingOptions[0]] : [{ label: this.labels.HubMenuSelectBuildings, value: -1 }, ...this.state.duplicateBuildingOptions.filter(option => option.value !== -1)],
            duplicateNotificationTitle: selectedAnnouncement?.title || "",
            startDateTime: selectedAnnouncement?.start ? selectedAnnouncement?.start : this.state.startDateTime,
            endDateTime: selectedAnnouncement?.end ? selectedAnnouncement?.end : this.state.endDateTime,
        });

    }

    private startDateTimeChanged(value: DateTime): void
    {
        if (value > this.state.endDateTime)
        {
            this.setState({ showDateTimeError: true });
        }
        else
        {
            this.setState({ showDateTimeError: false });
        }
        this.setState({ startDateTime: value });
    }

    private endDateTimeChanged(value: DateTime): void
    {
        if (value < this.state.startDateTime)
        {
            this.setState({ showDateTimeError: true });
        }
        else
        {
            this.setState({ showDateTimeError: false });
        }
        this.setState({ endDateTime: value });
    }

    private getBuildingName(buildingId: number): string
    {
        const building = this.state.duplicateBuildingOptions.find((duplicateBuildingOption) => duplicateBuildingOption.value === buildingId);
        return building?.label || "";
    }

    private deleteBuildingClicked(buildingId: number): void
    {
        if (this.isZeroOrOneBuildings)
        {
            return;
        }
        const updatedBuildings = this.state.selectedBuildings.filter((building) => buildingId !== building);
        this.setState({ selectedBuildings: updatedBuildings });
    }

    public render(): JSX.Element
    {
        const columns =
            [
                {
                    field: Helper.nameOf<AnnouncementView>("id"),
                    headerName: this.labels.HubLabelId,
                    flex: 1,
                    sortable: false
                },
                {
                    field: Helper.nameOf<AnnouncementView>("broadcast"),
                    headerName: this.labels.funcAnnouncementsBroadcast_Short,
                    flex: 1,
                    sortable: false
                },
                {
                    field: Helper.nameOf<AnnouncementView>("label"),
                    headerName: this.labels.HubLabelLabel,
                    flex: 1,
                    sortable: false
                },
                {
                    field: Helper.nameOf<AnnouncementView>("title"),
                    headerName: this.labels.HubLabelTitle,
                    flex: 1,
                    sortable: false
                },
                {
                    field: Helper.nameOf<AnnouncementView>("start"),
                    headerName: this.labels.HubLabelStart,
                    renderCell: (params: { row: AnnouncementView; }) => params.row.start.toLocaleDateTimeString(),
                    flex: 1,
                    sortable: false
                },
                {
                    field: Helper.nameOf<AnnouncementView>("end"),
                    headerName: this.labels.HubLabelEnd,
                    renderCell: (params: { row: AnnouncementView; }) => params.row.end.toLocaleDateTimeString(),
                    flex: 1,
                    sortable: false
                },
                {
                    field: Helper.nameOf<AnnouncementView>("priority"),
                    headerName: this.labels.HubLabelPriorityLabel,
                    flex: 1,
                    sortable: false,
                    renderCell: (params: { row: AnnouncementView; }) => this.getPriorityLabel(params.row.priority),
                },
                {
                    field: Helper.nameOf<AnnouncementView>("createdBy"),
                    headerName: this.labels.HubLabelCreatedBy,
                    flex: 1,
                    sortable: false,
                    renderCell: (params: { row: AnnouncementView; }) => params.row.createdBy ?? '-',
                },
                {
                    field: Helper.nameOf<AnnouncementView>("status"),
                    headerName: this.labels.HubLabelStatus,
                    renderCell: (params: { row: AnnouncementView; }) => this.renderNotificationStatus(params.row),
                    flex: 1,
                    sortable: false
                },
            ];

        const actionButtons: IActionButton[] =
            [
                {
                    label: this.labels.funcDuplicate,
                    icon: (
                        <IbssSvgIcon sx={{ marginTop: '4px' }}>
                            {Icons.RepeatIcon}
                        </IbssSvgIcon>
                    ),
                    color: "info",
                    onClick: () => this.duplicateAnnouncementClicked(),
                    disabled: !this.userCanCreate || this.state.selectedAnnouncements.length != 1,
                },
                {
                    label: this.labels.funcAnnouncementsExpire_Short,
                    icon: (
                        <IbssSvgIcon>
                            <PauseIcon />
                        </IbssSvgIcon>
                    ),
                    color: "info",
                    onClick: () => this.setState({ showExpireConfirmationModal: true }),
                    disabled: !this.userCanUpdate || this.state.selectedAnnouncements.length == 0,
                },
                {
                    label: this.labels.HubButtonDelete,
                    icon: (
                        <IbssSvgIcon>
                            <DeleteOutlinedIcon />
                        </IbssSvgIcon>
                    ),
                    color: "error",
                    onClick: () => this.setState({ showDeleteConfirmationModal: true }),
                    disabled: !this.userCanDelete || this.state.selectedAnnouncements.length == 0,
                },
                {
                    label: this.labels.HubLabelAddButton,
                    icon: <IbssSvgIcon>{Icons.AddIcon}</IbssSvgIcon>,
                    color: "info",
                    onClick: () => this.props.history.push("/oneLens/" + this.state.buildingId + "/announcements/create"),
                },
            ];

        return (
            <Box className="page-height-exct-header">
                <Box className="rightPanel-main-content">
                    {/* Filter Modal */}
                    <ListFilterModal
                        open={this.state.openFilterModal}
                        onClose={() => this.setState({ openFilterModal: false })}
                        broadcastSelectValue={this.state.broadcastSelectValue}
                        statusSelectValue={this.state.statusSelectValue}
                        selectedAnnouncementCreator={this.state.selectedAnnouncementCreatorEmail}
                        onBroadcastValueChanged={(value) => this.setState({ broadcastSelectValue: value })}
                        onStatusValueChanged={(value) => this.setState({ statusSelectValue: value })}
                        onAnnouncementCreatorTextChanged={(value) => this.setState({ selectedAnnouncementCreatorEmail: value })}
                        onAnnouncementCreatorChanged={user => this.setState({ selectedAnnouncementCreator: user })}
                        onSubmit={() => this.filterModalSubmitted()}
                    />
                    {/* Delete confirmation Modal */}
                    <IbssDialog
                        open={this.state.showDeleteConfirmationModal}
                        onClose={() => this.setState({ showDeleteConfirmationModal: false })}
                        fullWidth
                        header={this.labels.HubLabelModalConfirmTitle}
                        dialogContent=
                        {
                            <div>{this.labels.funcAnnouncementsConfirmDelete_Message}</div>
                        }
                        footer=
                        {
                            <>
                                <IbssButtonRedo
                                    color="primary"
                                    variant="contained"
                                    size="medium"
                                    onClick={() => this.deleteSelectedItems()}
                                >
                                    {this.labels.HubLabelOk}
                                </IbssButtonRedo>
                            </>
                        }
                    />
                    {/* Duplicate notification Modal */}
                    <IbssDialog
                        open={this.state.showDuplicateNotificationModal}
                        onClose={() => this.setState({ showDuplicateNotificationModal: false })}
                        fullWidth
                        header={this.labels.funcAnnouncementsCreateDuplicate_Short}
                        dialogContent=
                        {
                            <Box>
                                <Typography variant='body2' className='ui-text-light'>{this.labels.funcAnnouncementsDuplicate_D}</Typography>
                                <hr />
                                <Box>
                                    <Typography variant='body2' className='ui-text-light' mb={1}>{this.labels.funcAnnouncementSelected_S.toUpperCase()}</Typography>
                                    <Box className='card-secondary'>
                                        <Typography variant='body2' className='p-2'>{this.state.selectedAnnouncements[0]?.title}</Typography>
                                    </Box>
                                </Box>
                                <hr />
                                <Box>
                                    <Typography variant='body2' mb={1} className='ui-text-light'>{this.labels.funcAnnouncementDuplicateLocation_S.toUpperCase()}</Typography>
                                    <Box>
                                        <TextField
                                            select
                                            id="buildingDropdown"
                                            value={this.selectedBuildingId}
                                            disabled={this.isZeroOrOneBuildings}
                                            fullWidth
                                            sx={{
                                                '& legend': { display: 'none' },
                                                '& fieldset': { top: 0 },
                                            }}>
                                            {this.state.duplicateBuildingOptions.map(duplicateBuildingOption =>
                                                <MenuItem
                                                    key={duplicateBuildingOption.value}
                                                    value={duplicateBuildingOption.value}
                                                    onClick={() => this.buildingClicked(duplicateBuildingOption.value)}>
                                                    {duplicateBuildingOption.label}
                                                </MenuItem>
                                            )}
                                        </TextField>
                                    </Box>
                                </Box>
                                <Box className='my-2'>
                                    {this.state.selectedBuildings.map((building) =>
                                    {
                                        return (
                                            <IbssChip label={this.getBuildingName(building)} className='p-2 mr-2' onDelete={() => this.deleteBuildingClicked(building)} />
                                        )
                                    })}
                                </Box>
                                <hr />
                                <Box sx={{ '& > :not(style)': { my: 1 } }}>
                                    <Typography variant='body2' className='ui-text-light' mb={1.5}>{this.labels.funcAnnouncementEditDetails_S.toUpperCase()}</Typography>
                                    <IbssFormControl fullWidth className='mb-3'>
                                        <IbssTextField value={this.state.duplicateNotificationTitle} onChange={(e) => this.setState({ duplicateNotificationTitle: e.target.value })} label={this.labels.HubLabelTitle} />
                                    </IbssFormControl>
                                    <IbssFormControl className='mr-3' fullWidth>
                                        <IbssLuxonDateTimePicker
                                            label={this.labels.HubLabelStart}
                                            value={this.state.startDateTime}
                                            onChange={e => this.startDateTimeChanged(e)}
                                            input=
                                            {{
                                                error: this.state.showDateTimeError,
                                                helperText: this.state.showDateTimeError ? this.labels.funcAnnouncementStartError_Message : ''
                                            }}
                                        />
                                    </IbssFormControl>
                                    <IbssFormControl fullWidth>
                                        <IbssLuxonDateTimePicker
                                            label={this.labels.HubLabelEnd}
                                            value={this.state.endDateTime}
                                            onChange={e => this.endDateTimeChanged(e)}
                                            input=
                                            {{
                                                error: this.state.showDateTimeError,
                                                helperText: this.state.showDateTimeError ? this.labels.funcAnnouncementEndError_Message : ''
                                            }}
                                        />
                                    </IbssFormControl>
                                </Box>
                            </Box>
                        }
                        footer=
                        {
                            <>
                                <IbssButtonRedo
                                    color="primary"
                                    variant="contained"
                                    size="medium"
                                    onClick={() => this.createDuplicate()}
                                    disabled={this.state.selectedBuildings.length === 0}
                                >
                                    {this.labels.funcDuplicate}
                                </IbssButtonRedo>
                            </>
                        }
                    />
                    {/* Expire confirmation Modal */}
                    <IbssDialog
                        open={this.state.showExpireConfirmationModal}
                        onClose={() => this.setState({ showExpireConfirmationModal: false })}
                        fullWidth
                        header={this.labels.HubLabelModalConfirmTitle}
                        dialogContent=
                        {
                            <div>{this.labels.funcAnnouncementsConfirmExpire_Message}</div>
                        }
                        footer=
                        {
                            <>
                                <IbssButtonRedo
                                    color="primary"
                                    variant="contained"
                                    size="medium"
                                    onClick={() => this.expireSelectedItems()}
                                >
                                    {this.labels.HubLabelOk}
                                </IbssButtonRedo>
                            </>
                        }
                    />
                    <Box className="table-panel">
                        <IbssFilterHeader
                            title={this.labels.funcAnnouncementsSheduledContent_Short}
                            description= {this.labels.funcAnnouncementsTableDescriptionPt1_Message}
                            selectedPreset={this.state.dateRangeFilter}
                            presetChanged={(e) => this.dateRangeChanged(e)}
                            startDate={this.state.fromFilter.toJSDate()}
                            endDate={this.state.toFilter.toJSDate()}
                            datesDisabled={this.state.datePickerFiltersDisabled}
                            onStartDateChange={i => this.startDateChanged(i)}
                            onEndDateChange={i => this.endDateChanged(i)}
                            presets={[this.labels.HubLabelTomorrow, this.labels.HubLabelThisWeek, this.labels.HubLabelNextWeek, this.labels.HubLabelThisMonth, this.labels.HubLabelNextMonth]}
                        />
                        <div>
                            <IbssFilter
                                searchTerm={this.state.searchTerm}
                                onSearchTermChange={(event) => this.searchTermChange(event)}
                                onFilterButtonClick={() => this.setState({ openFilterModal: true })}
                            />
                            <Box component="div" sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center', mr: 0 }}>
                                <IbssActionButton buttons={actionButtons} />
                            </Box>
                            <ServerDataGrid 
                                ref={this.dataGridRef}
                                checkboxSelection
                                columns={columns}
                                disableRowSelectionOnClick
                                loadDataOnMount={false}
                                onDataQueryChange={query => this.dataQueryChanged(query)}
                                onPagesChange={pages => this.setStateAsync({ pagesOfAnnouncements: pages })}
                                onRowSelectionModelChange={e => this.handleSelectionChange(e)}
                                pages={this.state.searchTerm? this.filterAnnouncements(this.state.searchTerm): this.state.pagesOfAnnouncements}
                                pageSizeOptions={[this.pageSize]}
                                rowSelectionModel={this.state.selectedAnnouncements.map((announcement) => announcement.id)}
                                onRowClick={row => this.props.history.push("/oneLens/" + this.state.buildingId + "/announcements/edit/" + row.id)}
                            />
                        </div>
                    </Box>
                    </Box>
            </Box>
        )
    }
}

export default ListNotifications;

export interface IProps extends RouteComponentProps<IQueryParams>
{
}

export interface IState
{
    loading: boolean,
    pagesOfAnnouncements: IPages<AnnouncementView>,
    dateRangeFilter: string,
    fromFilter: DateTime,
    toFilter: DateTime,
    datePickerFiltersDisabled: boolean,
    searchTerm: string,
    openFilterModal: boolean
    broadcastSelectValue: string;
    statusSelectValue: string;
    selectedAnnouncementCreatorEmail: string;
    selectedAnnouncementCreator: IUser | null;
    showDeleteConfirmationModal: boolean;
    buildingId: number,
    showDuplicateNotificationModal: boolean,
    duplicateNotificationTitle: string,
    showExpireConfirmationModal: boolean,
    selectedAnnouncements: AnnouncementView[],
    duplicateBuildingOptions: IOption[];
    startDateTime: DateTime;
    endDateTime: DateTime;
    showDateTimeError: boolean;
    selectedBuildings: number[];
}

interface IOption
{
    label: string;
    value: number;
}

export interface IQueryParams
{
    buildingid: string;
}

class AnnouncementView
{
    public id = '';
    public title = '';
    public priority = 0;
    public recordId = '';
    public broadcast = '';
    public target = 0;
    public label = '';
    public message = '';
    public shortMessage = '';
    public reminders =  "";
    public type = 0;
    public status = '';
    public createdBy = '';
    public sent = 0;
    public device = null;
    public classification = '';
    public subClassification = '';
    public start = DateHelper.null();
    public end = DateHelper.null();
    public hasImage = 0;
    public imageUri = '';
    public actionURI = '';
    public nodeId = 0;

    public static getBroadcastTarget(target: number | null): string
    {
        const labels = appContext().labels;
        switch(target)
        {
            case 0:
                return labels.HubLabelNone;
            case 1:
                return labels.funcAnnouncementRoamerAndFlex;
            case 2:
                return labels.HubMenuInspire;
            case 4:
                return labels.HubMenuUnity;
            case 8:
                return labels.HubLabelGuest;
            default:
                return labels.HubLabelNone;
        }
    }

    public static fromAnnouncementToView(announcement: Announcement): AnnouncementView
    {
        return {
            id: `${announcement.Notification_Id}`,
            nodeId: announcement.Node_Id,
            title: announcement.Notification_Title,
            priority: announcement.Notification_Priority,
            recordId: announcement.Notification_Record_Id,
            broadcast: this.getBroadcastTarget(announcement.Notification_Target),
            target: announcement.Notification_Target,
            label: announcement.Subclassification === 'INSPIRE' ? '-' : announcement.Subclassification,
            message: announcement.Notification_Message,
            shortMessage: announcement.Notification_Short_Message,
            reminders: announcement.Notification_Reminders,
            type: announcement.Notification_Type,
            status: announcement.Notification_Status,
            createdBy: announcement.CreatedBy,
            sent: announcement.Notification_Sent,
            device: announcement.Notification_Device,
            classification: announcement.Classification,
            subClassification: announcement.Subclassification,
            start: announcement.Available_From,
            end: announcement.Expiry_Date,
            hasImage: announcement.Has_Image,
            imageUri: announcement.ImageURI,
            actionURI: announcement.ActionURI,
        }
    }
}